import { Fragment, useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, IconButton,
    Checkbox, FormControlLabel,
    Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const PLANS = [
    { id: 'my_health_indonesia_elite',     key: 'elite',     label: 'Elite',     hsKey: 'Elite' },
    { id: 'my_health_indonesia_extensive', key: 'extensive', label: 'Extensive', hsKey: 'Extensive' },
    { id: 'my_health_indonesia_essential', key: 'essential', label: 'Essential', hsKey: 'Essential' },
    { id: 'my_health_indonesia_core',      key: 'core',      label: 'Core',      hsKey: 'Core' },
];

const OPTIONAL_COVERS = [
    { key: 'op', label: 'Outpatient', rateKey: 'op', premiumField: 'op_premium' },
    { key: 'ma', label: 'Maternity',  rateKey: 'ma', premiumField: 'ma_premium' },
    { key: 'dn', label: 'Dental',     rateKey: 'dn', premiumField: 'dn_premium' },
];

const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];
const BRAND_COLOR = '#004161';
const BRAND_HOVER = '#003049';
const BRAND_SUBTITLE = '#6a9a4a';
const BRAND_ACCENT_TEXT = '#002047';
const DEFAULT_COVER_PLANS = { op: 'N/A', ma: 'N/A', dn: 'N/A' };

// Legacy plans stored a single optional_covers list rather than per-component plans;
// they always used the base HS plan (Core has no maternity, so MA fell back to Essential).
function legacyCoverPlanKey(basePlanId, coverKey) {
    const planObj = PLANS.find(p => p.id === basePlanId) || PLANS[0];
    if (coverKey === 'ma' && planObj.hsKey === 'Core') return 'Essential';
    return planObj.hsKey;
}

function formatRate(rate) {
    if (rate === null || rate === undefined || rate === '') return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return '$' + rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function MyHealthIndonesia_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const idConfig          = appConfig?.content?.specifics?.my_health_indonesia || {};
    const areaOptions       = appConfig?.content?.set4_area_of_coverage || [];
    const defaultArea       = areaOptions.includes('Worldwide excl USA') ? 'Worldwide excl USA' : (areaOptions[0] || '');
    const deductibleOptions = appConfig?.content?.deductibles?.my_health_indonesia?.['USD'] || [];
    const coInsOptions      = Object.keys(idConfig?.co_ins || {});

    const [selectedPlan, setSelectedPlan]         = useState('my_health_indonesia_elite');
    // { op: planName|'N/A', ma: planName|'N/A', dn: planName|'N/A' }
    const [coverPlans, setCoverPlans]             = useState(DEFAULT_COVER_PLANS);
    const [areaOfCoverage, setAreaOfCoverage]     = useState(defaultArea);
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    // { [quoteName]: { deductible: '', co_insurance: '' } }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    // Set(clientUid) — which eligible females get maternity, when 2+ are eligible. Applies to all quotes.
    const [maternityClients, setMaternityClients] = useState(null);
    const [ratesData, setRatesData]               = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    const [loading, setLoading]                   = useState(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const initializingFromEdit = useRef(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedPlanObj  = PLANS.find(p => p.id === selectedPlan) || PLANS[0];
    const planOptions       = idConfig?.plans || [];
    // Core has no Maternity plan
    const maPlanOptions     = planOptions.filter(p => p !== 'Core');
    const coverPlanOptions  = { op: planOptions, ma: maPlanOptions, dn: planOptions };
    const activeCovers      = OPTIONAL_COVERS.filter(c => coverPlans[c.key] && coverPlans[c.key] !== 'N/A');
    const familyDiscount    = idConfig?.family_discount;
    // Same "Area of Coverage" note as MyHealthIndonesia_Rates' getAreaOfCoverage
    const areaOfCover = appConfig?.content?.specifics?.my_health_indonesia?.area_of_cover?.[areaOfCoverage] || '';
    const eligibleFemales   = clients.filter(isMaternityEligible);

    // When 2+ clients are eligible for maternity, the user picks who it applies to (applies to all quotes).
    // With 0 or 1 eligible clients there's nothing to choose, so it applies automatically.
    function isMaternitySelectedForClient(client) {
        if (!isMaternityEligible(client)) return false;
        if (eligibleFemales.length < 2) return true;
        return maternityClients ? maternityClients.has(client.uid) : true;
    }

    function toggleMaternityClient(clientUid) {
        setMaternityClients(prev => {
            const current = prev ? new Set(prev) : new Set(eligibleFemales.map(c => c.uid));
            if (current.has(clientUid)) current.delete(clientUid);
            else current.add(clientUid);
            return current;
        });
        invalidateRates();
    }

    function getFamilyDiscountLabel() {
        const clientsCount = clients.length;
        if (!familyDiscount) return '0%';
        const discount = clientsCount > 7
            ? (familyDiscount['>7'] || 0)
            : (familyDiscount[clientsCount] || 0);
        return `${discount * 100}%`;
    }

    // Maternity is only available with a Nil deductible, Elite/Extensive HS, and an Outpatient plan.
    // (Per-client age/gender eligibility is handled automatically — ineligible clients get 'N/A' plan.)
    // Core plan rules are the same as MyHealthIndonesia_Rates' validateCorePlan.
    function getValidationMessages() {
        const messages = [];
        const allNilDeductible = (quotes || []).filter(q => !isBlankQuote(q)).every(q => (quoteDeductibles[q.name]?.deductible || '') === 'Nil');

        if (coverPlans.ma !== 'N/A') {
            const hsEligible = ['Elite', 'Extensive'].includes(selectedPlanObj.hsKey);
            const opSelected = coverPlans.op !== 'N/A';

            if (!hsEligible || !opSelected || !allNilDeductible) {
                messages.push('Maternity is only available if Nil deductible, Elite or Extensive Hospital and Surgery, and an Outpatient plan have been selected. Please change your selection.');
            }
        }

        const isCoreHs = selectedPlanObj.hsKey === 'Core';
        if (isCoreHs || coverPlans.op === 'Core') {
            if (coverPlans.op !== 'N/A' && coverPlans.op !== selectedPlanObj.hsKey) {
                messages.push('You must select Core Outpatient with Core Inpatient. Please change your selection.');
            }
            if (['Worldwide excl USA', 'Worldwide'].includes(areaOfCoverage)) {
                messages.push('Worldwide Excluding U.S.A and Worldwide are not available for Core Plan. Please change your selection.');
            }
        }
        if (isCoreHs && !allNilDeductible) {
            messages.push('Deductibles are not available if Core plan has been selected. Please change your selection in Hospital & Surgery Plan.');
        }

        return messages;
    }

    const validationMessages = getValidationMessages();

    function getDefaultQuoteDeductibles() {
        const defaults = {};
        (quotes || []).forEach(q => {
            defaults[q.name] = {
                deductible:   deductibleOptions[0] || '',
                co_insurance: coInsOptions[0]       || '',
            };
        });
        return defaults;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            initializingFromEdit.current = true;
            const basePlanId = editingPlan.base_plan_id || editingPlan.plan_id;
            setSelectedPlan(basePlanId);

            const legacyCovers = new Set(editingPlan.payloads?.optional_covers || []);
            setCoverPlans({
                op: editingPlan.payloads?.op_plan ?? (legacyCovers.has('op') ? legacyCoverPlanKey(basePlanId, 'op') : 'N/A'),
                ma: editingPlan.payloads?.ma_plan ?? (legacyCovers.has('ma') ? legacyCoverPlanKey(basePlanId, 'ma') : 'N/A'),
                dn: editingPlan.payloads?.dn_plan ?? (legacyCovers.has('dn') ? legacyCoverPlanKey(basePlanId, 'dn') : 'N/A'),
            });
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid]
                    || { deductible: '', co_insurance: '' };
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData (and which clients had maternity applied) from stored premiums
            const ratesMap = {};
            const maternitySelections = new Set();
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    const maPremium = premium?.ma_premium ?? null;
                    if (maPremium !== null && maPremium !== 'N/A') maternitySelections.add(client.uid);
                    return {
                        hs: premium?.hs_premium ?? null,
                        op: premium?.op_premium ?? null,
                        ma: maPremium,
                        dn: premium?.dn_premium ?? null,
                        wd: premium?.wd_premium ?? null,
                    };
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
            setMaternityClients(maternitySelections.size > 0 ? maternitySelections : null);
            setTimeout(() => { initializingFromEdit.current = false; }, 0);
        } else if (open && !editingPlan) {
            setAreaOfCoverage(defaultArea);
            setPaymentFrequency('Annually');
            setCoverPlans(DEFAULT_COVER_PLANS);
            setQuoteDeductibles(getDefaultQuoteDeductibles());
            setMaternityClients(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Ensure co-insurance has a default value even if hidden, to prevent validation errors
    useEffect(() => {
        if (open && coInsOptions.length > 0) {
            setQuoteDeductibles(prev => {
                let changed = false;
                const next = { ...prev };
                (quotes || []).forEach(q => {
                    if (!next[q.name]?.co_insurance) {
                        next[q.name] = { ...(next[q.name] || {}), co_insurance: coInsOptions[0] };
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
        }
    }, [open, coInsOptions, quotes]);

    // Clear rates when plan changes
    useEffect(() => {
        if (initializingFromEdit.current) return;
        invalidateRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPlan]);

    function setCoverPlan(key, value) {
        setCoverPlans(prev => ({ ...prev, [key]: value }));
        invalidateRates();
    }

    function setQuoteField(quoteName, field, value) {
        setQuoteDeductibles(prev => ({
            ...prev,
            [quoteName]: { ...(prev[quoteName] || {}), [field]: value },
        }));
        setDeductibleErrors(prev => ({
            ...prev,
            [quoteName]: { ...(prev[quoteName] || {}), [field]: false },
        }));
        invalidateRates();
    }

    function isMaternityEligible(client) {
        return client.gender === 'Female' && Number(client.age) >= 19 && Number(client.age) <= 45;
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name]?.deductible === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (isBlankQuote(q)) return;
            const d = quoteDeductibles[q.name];
            const qErrors = {};
            if (!d?.deductible)   qErrors.deductible   = true;
            // Only validate co-insurance if Outpatient (op) is selected
            if (coverPlans.op !== 'N/A' && !d?.co_insurance) qErrors.co_insurance = true;
            if (Object.keys(qErrors).length > 0) errors[q.name] = qErrors;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please complete deductibles for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { my_health_indonesia: quoteDeductibles[quote.name]?.deductible || '' },
                            insurers: ['my_health_indonesia'],
                            specifics: {
                                my_health_indonesia: clients.map((client) => ({
                                    hs: {
                                        plan:      selectedPlanObj.hsKey,
                                        deductible: quoteDeductibles[quote.name]?.deductible || '',
                                    },
                                    op: {
                                        plan:   coverPlans.op,
                                        co_ins: quoteDeductibles[quote.name]?.co_insurance || (coInsOptions[0] || ''),
                                    },
                                    ma: {
                                        plan: coverPlans.ma !== 'N/A' && isMaternitySelectedForClient(client) ? coverPlans.ma : 'N/A',
                                    },
                                    dn: {
                                        plan: coverPlans.dn,
                                    },
                                })),
                            },
                            specific_area_of_coverage:  { my_health_indonesia: areaOfCoverage },
                            rates_version_per_insurer:  { my_health_indonesia: selectedRatesVersion?.my_health_indonesia || '' },
                            specific_currency:          { my_health_indonesia: 'USD' },
                            specific_payment_frequency: { my_health_indonesia: paymentFrequency },
                        },
                    }).then(response => ({ quote, data: response?.data?.my_health_indonesia }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quote, data, blank }) => {
                if (blank) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map((r, idx) => ({
                        hs: r.hs ?? null,
                        op: r.op ?? null,
                        ma: coverPlans.ma !== 'N/A' && !isMaternitySelectedForClient(clients[idx]) ? 'N/A' : (r.ma ?? null),
                        dn: r.dn ?? null,
                        wd: r.wd ?? null,
                    }));
                    ratesMap[quote.uid] = { rawRates };
                } else {
                    ratesMap[quote.uid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching MyHealth Indonesia rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    // Client subtotal = HS + active optional covers - Welcome Discount
    function getClientSubtotal(clientIndex, quoteUid) {
        const raw = ratesData?.[quoteUid]?.rawRates?.[clientIndex];
        if (!raw) return null;
        let total = 0;
        let hasAny = false;
        if (typeof raw.hs === 'number') { total += raw.hs; hasAny = true; }
        for (const cover of activeCovers) {
            const r = raw[cover.rateKey];
            if (typeof r === 'number') { total += r; hasAny = true; }
        }
        if (typeof raw.wd === 'number' && raw.wd !== 0) { total -= raw.wd; hasAny = true; }
        return hasAny ? total : null;
    }

    // Column subtotal (sum across all clients for a given rate key)
    function getColumnSubtotal(rateKey, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = ratesData[quoteUid].rawRates[i]?.[rateKey];
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function hasWelcomeDiscount(quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return false;
        return ratesData[quoteUid].rawRates.some(r => typeof r.wd === 'number' && r.wd !== 0);
    }

    // Total premium for the quote (sum of all client subtotals)
    function getQuoteTotalPremium(quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const sub = getClientSubtotal(i, quoteUid);
            if (typeof sub === 'number') { total += sub; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function buildPlanLabel() {
        let label = `HS ${selectedPlanObj.label}`;
        if (coverPlans.op !== 'N/A') label += ` + OP ${coverPlans.op}`;
        if (coverPlans.ma !== 'N/A') label += ` + MA ${coverPlans.ma}`;
        if (coverPlans.dn !== 'N/A') label += ` + DN ${coverPlans.dn}`;
        return label;
    }

    function buildDeductibleString(quoteName) {
        const d = quoteDeductibles[quoteName];
        return d?.deductible || '';
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const coversSuffix = OPTIONAL_COVERS
            .filter(c => coverPlans[c.key] !== 'N/A')
            .map(c => `${c.key}_${coverPlans[c.key]}`)
            .join('_')
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, '');
        const effectivePlanId = editingPlan
            ? editingPlan.plan_id
            : (coversSuffix ? `${selectedPlan}_${coversSuffix}` : selectedPlan);
        // plan_id identifies the tier + covers combination and is shared by every instance of it;
        // uid identifies this specific plan instance so multiple plans of the same combination
        // can coexist instead of overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            const totalPremium = getQuoteTotalPremium(quote.uid);

            clients.forEach((client, clientIndex) => {
                const rawRates       = ratesData[quote.uid]?.rawRates?.[clientIndex];
                const clientSubtotal = getClientSubtotal(clientIndex, quote.uid);
                updates.push({
                    quoteUid:  quote.uid,
                    clientUid: client.uid,
                    planId:    effectivePlanId,
                    planUid,
                    premium: {
                        insurer:           'my_health_indonesia',
                        plan:              selectedPlanObj.key,
                        plan_label:        planLabel,
                        included_benefits: '',
                        deductible:        buildDeductibleString(quote.name),
                        co_insurance:      quoteDeductibles[quote.name]?.co_insurance || '',
                        area_of_coverage:  areaOfCoverage,
                        currency:          'USD',
                        payment_frequency: paymentFrequency,
                        optional_covers:   activeCovers.map(c => c.key),
                        op_plan:           coverPlans.op,
                        ma_plan:           coverPlans.ma,
                        dn_plan:           coverPlans.dn,
                        premium_rate:      clientSubtotal ?? null,
                        hs_premium:        rawRates?.hs ?? null,
                        op_premium:        rawRates?.op ?? null,
                        ma_premium:        rawRates?.ma ?? null,
                        dn_premium:        rawRates?.dn ?? null,
                        wd_premium:        rawRates?.wd ?? null,
                        total_premium:     totalPremium ?? null,
                    },
                });
            });
        });

        const plan = {
            plan_id:      effectivePlanId,
            uid:          planUid,
            base_plan_id: selectedPlan,
            plan:         selectedPlanObj.key,
            insurer:      'my_health_indonesia',
            label:    `April (MyHEALTH) Indonesia ${planLabel}`,
            payloads: {
                area_of_coverage:  areaOfCoverage,
                currency:          'USD',
                payment_frequency: paymentFrequency,
                optional_covers:   activeCovers.map(c => c.key),
                op_plan:           coverPlans.op,
                ma_plan:           coverPlans.ma,
                dn_plan:           coverPlans.dn,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || {}])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    height: 60,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.2,
                    fontWeight: 600,
                }}><span style={{ color: '#fff' }}>MyHEALTH</span> <span style={{ color: BRAND_SUBTITLE }}>Indonesia</span></div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={`${process.env.PUBLIC_URL}/images/Asuransi Logo.png`} alt="Asuransi Insurance" style={{ height: 50, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <img src={`${process.env.PUBLIC_URL}/images/Arta Insurance Logo.png`} alt="Arta Insurance" style={{ height: 50, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <img src={`${process.env.PUBLIC_URL}/images/April International Logo.png`} alt="April International" style={{ height: 50, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type — Hospital & Surgery */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center' }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Hospital & Surgery</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={selectedPlan}
                                disabled={loading || !!editingPlan}
                                // Only the selected plans are priced, so a new plan needs new rates
                                onChange={(e) => { setSelectedPlan(e.target.value); invalidateRates(); }}
                                sx={selectSx}
                            >
                                {PLANS.map(plan => (
                                    <MenuItem key={plan.id} value={plan.id} sx={menuItemSx}>{plan.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Optional covers — Outpatient / Maternity / Dental */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Optional Covers</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center' }}>
                        {OPTIONAL_COVERS.map(cover => (
                            <Fragment key={cover.key}>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>{cover.label}</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    <Select
                                        value={coverPlans[cover.key]}
                                        disabled={loading || !!editingPlan}
                                        onChange={(e) => setCoverPlan(cover.key, e.target.value)}
                                        sx={selectSx}
                                    >
                                        <MenuItem value="N/A" sx={menuItemSx}>N/A</MenuItem>
                                        {coverPlanOptions[cover.key].map(p => (
                                            <MenuItem key={p} value={p} sx={menuItemSx}>{p}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Fragment>
                        ))}
                    </Box>
                    {coverPlans.ma !== 'N/A' && eligibleFemales.length >= 2 && (
                        <Box sx={{ mt: 1.5, maxWidth: 320 }}>
                            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Maternity for</Typography>
                            {eligibleFemales.map(client => (
                                <FormControlLabel
                                    key={client.uid}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={isMaternitySelectedForClient(client)}
                                            onChange={() => toggleMaternityClient(client.uid)}
                                            disabled={loading}
                                            sx={{ p: 0.5 }}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: 13 }}>{client.full_name || 'Client'}</Typography>}
                                    sx={{ mr: 1.5 }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Options — same as MyHealthIndonesia_Rates */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2, maxWidth: 420 }}>
                        {areaOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area Of Coverage</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    <Select value={areaOfCoverage} disabled={loading} onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }} sx={selectSx}>
                                        {areaOptions.map(area => <MenuItem key={area} value={area} sx={menuItemSx}>{area}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value="USD" disabled sx={selectSx}>
                                <MenuItem value="USD" sx={menuItemSx}>USD</MenuItem>
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={paymentFrequency} disabled={loading} onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {PAYMENT_FREQUENCIES.map(f => <MenuItem key={f} value={f} sx={menuItemSx}>{f}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Typography sx={{ gridColumn: '1 / -1', fontSize: 13 }}>
                            Family Discount: <span style={{ color: BRAND_ACCENT_TEXT, fontWeight: 600 }}>{getFamilyDiscountLabel()}</span>
                        </Typography>
                        {areaOfCover && (
                            <Typography sx={{ gridColumn: '1 / -1', fontSize: 13 }}>
                                Area of Coverage: <span style={{ color: BRAND_ACCENT_TEXT, fontWeight: 600 }}>{areaOfCover}</span>
                            </Typography>
                        )}
                </Box>
                </Box>

                {validationMessages.length > 0 && (
                    <Box component="ul" sx={{ m: 0, mb: 2, pl: 3, color: '#d32f2f' }}>
                        {validationMessages.map((message, idx) => (
                            <Typography component="li" key={idx} sx={{ fontSize: 13 }}>{message}</Typography>
                        ))}
                    </Box>
                )}

                <Divider sx={{ mb: 2 }} />

                {/* Deductibles per Quote */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{quote.name}</Typography>
                                <Box sx={{ display: 'flex', gap: 2, pl: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: 70 }}>Deductible</Typography>
                                        <CustomSelect2
                                            dense
                                            value={quoteDeductibles[quote.name]?.deductible || ''}
                                            onChange={(e) => setQuoteField(quote.name, 'deductible', e.target.value)}
                                            items={[LEAVE_BLANK, ...deductibleOptions]}
                                            disabled={loading}
                                            error={!!deductibleErrors[quote.name]?.deductible}
                                            sx={{ minWidth: 200 }}
                                        />
                                    </Box>
                                    {coInsOptions.length > 0 && coverPlans.op !== 'N/A' && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: 55 }}>Outpatient Co-ins.</Typography>
                                            <CustomSelect2
                                                dense
                                                value={quoteDeductibles[quote.name]?.co_insurance || ''}
                                                onChange={(e) => setQuoteField(quote.name, 'co_insurance', e.target.value)}
                                                items={coInsOptions}
                                                disabled={loading || isBlankQuote(quote)}
                                                error={!!deductibleErrors[quote.name]?.co_insurance}
                                                isZeroDeductibleUniform={false}
                                                sx={{ minWidth: 130 }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: BRAND_COLOR, textTransform: 'none', '&:hover': { backgroundColor: BRAND_HOVER }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {/* Loading */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
                    </Box>
                )}

                {/* Rates Display */}
                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => {
                            const d = quoteDeductibles[quote.name];
                            const summary = isBlankQuote(quote) ? LEAVE_BLANK : [d?.deductible, (coverPlans.op !== 'N/A' && d?.co_insurance) ? `Co-ins: ${d.co_insurance}` : ''].filter(Boolean).join(' | ');
                            const totalPremium = getQuoteTotalPremium(quote.uid);
                            const wdSubtotal   = getColumnSubtotal('wd', quote.uid);
                            const showWD       = hasWelcomeDiscount(quote.uid);

                            return (
                                <Box key={quote.name} sx={{ mb: 3 }}>
                                    <Typography fontWeight={600} sx={{ mb: 1 }}>
                                        {quote.name}
                                        <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                            — {summary}
                                        </Typography>
                                    </Typography>
                                    {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 180 }}>Module</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                            <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                            <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 110 }}>Subtotal</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {/* HS row */}
                                                <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>Hospital & Surgery ({selectedPlanObj.label})</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(ratesData[quote.uid]?.rawRates?.[idx]?.hs)}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(getColumnSubtotal('hs', quote.uid))}
                                                    </TableCell>
                                                </TableRow>
                                                {/* Optional cover rows */}
                                                {activeCovers.map((cover, coverIdx) => (
                                                    <TableRow key={cover.key} sx={{ backgroundColor: (coverIdx + 1) % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                        <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                            {cover.label} ({coverPlans[cover.key]})
                                                        </TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx}>
                                                                {formatRate(ratesData[quote.uid]?.rawRates?.[idx]?.[cover.rateKey])}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 500 }}>
                                                            {formatRate(getColumnSubtotal(cover.rateKey, quote.uid))}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {/* Welcome Discount row */}
                                                {showWD && (
                                                    <TableRow sx={{ backgroundColor: '#fff8e1' }}>
                                                        <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Welcome Discount</TableCell>
                                                        {clients.map((client, idx) => {
                                                            const wd = ratesData[quote.uid]?.rawRates?.[idx]?.wd;
                                                            return (
                                                                <TableCell key={client.uid || idx} sx={{ color: '#388e3c' }}>
                                                                    {typeof wd === 'number' && wd !== 0 ? `-${formatRate(wd)}` : '-'}
                                                                </TableCell>
                                                            );
                                                        })}
                                                        <TableCell sx={{ fontWeight: 500, color: '#388e3c' }}>
                                                            {wdSubtotal ? `-${formatRate(wdSubtotal)}` : '-'}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {/* Client subtotal row */}
                                                <TableRow sx={{ backgroundColor: '#e8f4f8' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>Annual Premium (USD)</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                            {formatRate(getClientSubtotal(idx, quote.uid))}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        {formatRate(totalPremium)}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Paper>
)}
                                </Box>
                            );
                        })}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="my_health_indonesia" />

                <RatesVersionSelect
                    insurer="my_health_indonesia"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />
                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: BRAND_COLOR, textTransform: 'none', '&:hover': { backgroundColor: BRAND_HOVER }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
